import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Spin, theme } from "antd";
import { useTranslation } from "react-i18next";

/**
 * Covers the whole screen while a slow call to SooCool runs (client request 2026-10-16): the export
 * and the label PDFs can take a while, and a spinning button alone is easy to miss. Staff then click
 * on, export again, or close the tab while the shipments are still being created.
 *
 * `task` is `{ title, detail }`, or null when nothing runs. The page decides when it shows; this
 * component only blocks clicks and the keyboard, and asks the browser to confirm closing the tab.
 */
const BusyOverlay = ({ task }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const cardRef = useRef(null);
  const active = !!task;

  useEffect(() => {
    if (!active) return undefined;
    // Takes keyboard focus away from the button that started it, so Enter cannot start it twice.
    if (cardRef.current) cardRef.current.focus();
    const confirmLeave = (event) => {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", confirmLeave);
    return () => window.removeEventListener("beforeunload", confirmLeave);
  }, [active]);

  if (!task) return null;

  return createPortal(
    <div
      data-testid="busy-overlay"
      style={{
        position: "fixed",
        inset: 0,
        // Above the layout, drawers and dropdowns (1000-1050), below antd's messages and
        // notifications (2010+), so the result of the call stays readable when it lands.
        zIndex: 1100,
        backgroundColor: token.colorBgMask,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        ref={cardRef}
        tabIndex={-1}
        role="alertdialog"
        aria-modal="true"
        aria-busy="true"
        aria-labelledby="busy-overlay-title"
        aria-describedby="busy-overlay-hint"
        onKeyDown={(event) => {
          // Keep focus here: nothing behind the overlay can be reached until the call is done.
          if (event.key === "Tab") event.preventDefault();
        }}
        style={{
          backgroundColor: token.colorBgElevated,
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          padding: "24px 32px",
          width: "100%",
          maxWidth: 360,
          textAlign: "center",
          outline: "none",
        }}
      >
        <Spin size="large" />
        <div id="busy-overlay-title" style={{ marginTop: 16, fontWeight: 600, fontSize: 15, color: token.colorText }}>
          {task.title}
        </div>
        {!!task.detail && <div style={{ marginTop: 4, color: token.colorTextSecondary }}>{task.detail}</div>}
        <div id="busy-overlay-hint" style={{ marginTop: 12, fontSize: 12, color: token.colorTextTertiary }}>
          {t("busy.hint")}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BusyOverlay;
